<template>
  <div class="checklist-item-wrapper">
    <div class="checklist-item" :class="{ complete, active, pending }">
      <div class="check-header">
        <div class="check-title" v-if="showCheckbox">
          <div class="check">{{ complete ? "✓" : "" }}</div>
          {{ item.formattedTitle }}
          <p class="status hide">
            {{ status }}
          </p>
        </div>
        <div v-else>
          <div class="check-title">
            {{ item.formattedTitle }}
            <p class="status">{{ status }}</p>
          </div>
        </div>
        <button v-if="!complete" type="button" class="toggle" @click="toggleActive($event)">
          {{ active ? "-" : "+" }}
        </button>
      </div>
      <div class="check-content">
        <template v-if="item?.title === 'Income Verification'">
          <Income
            v-if="showIncomeSourcesSelector === true"
            :applicant="applicant"
            :application="application"
            @incomeSourcesUpdated="$emit('incomeSourcesUpdated')"
          />
          <div>
            <application-checklist-sub-item
              v-for="(subitem, index) in store.state.checklistModule.sortedApplicationStips[
                'Income Verification'
              ]"
              :key="index"
              :subitem="subitem"
              :title="getSubItemTitle(subitem)"
              :active="active"
              :itemComplete="item.complete"
              @handleUploadSuccess="$emit('handleUploadSuccess', $event)"
              @addressMismatchStipUpdated="$emit('addressMismatchStipUpdated', $event)"
            />
          </div>
        </template>
        <template v-else>
          <application-checklist-sub-item
            v-for="(subitem, index) in store.state.checklistModule.sortedApplicationStips[
              item.title
            ]"
            :key="index"
            :subitem="subitem"
            :title="getSubItemTitle(subitem)"
            :active="active"
            :itemComplete="item.complete"
            @handleUploadSuccess="$emit('handleUploadSuccess', $event)"
            @addressMismatchStipUpdated="$emit('addressMismatchStipUpdated', $event)"
						@stipulationUpdated="$emit('stipulationUpdated', $event)"
          />
        </template>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import ApplicationChecklistSubItem from "@/components/ApplicationChecklistSubItem.vue";
import Income from "@/components/checklist/Income.vue";
import { StipulationStatusIds } from "@/config/constants";
import type { SortedCheckListItem } from "@/models/checklist";
import type { Applicant, Application } from "@/models/application";

interface Props {
  item: SortedCheckListItem;
}

const props = withDefaults(defineProps<Props>(), {});
const store = useStore();
const showCheckbox = ref<boolean>(false);

const application = computed<Application>(() => store.state.appData?.application);
const applicant = computed<Applicant>(() => store.state.appData?.currentApplicant);
const active = computed<boolean>(() => props.item.status !== StipulationStatusIds.COMPLETED);
const complete = computed<boolean>(() => props.item.status === StipulationStatusIds.COMPLETED);
const pending = computed<boolean>(() => props.item.status === StipulationStatusIds.PENDING);
const showIncomeSourcesSelector = computed<boolean>(
  () => store.state.checklistModule?.showIncomeVerification === true,
);

const emit = defineEmits([
  "handleUploadSuccess",
  "incomeSourcesUpdated",
  "addressMismatchStipUpdated",
	"stipulationUpdated"
]);

function toggleActive(e: any) {
  const item = e.target.parentElement.parentElement;
  if (item) {
    item.classList.toggle("active");
    if (item.classList.value.indexOf("active") > -1) {
      e.target.innerHTML = "-";
    } else {
      e.target.innerHTML = "+";
    }
  }
}

function getSubItemTitle(subitem: any) {
  if (
    subitem.processingCategoryName === "Identity" ||
    subitem.processingCategoryName === "SpousalConsent" ||
    subitem.processingCategoryName === "Income" ||
    subitem.processingCategoryName === "Citizenship"
  ) {
    return subitem.stipulationTypeDescription;
  } else {
    return props.item.formattedTitle;
  }
}

const status = computed<string>(() => {
  switch (props.item.status) {
    case StipulationStatusIds.PENDING:
      return "Pending Review";
    case StipulationStatusIds.COMPLETED:
      return "Complete!";
    default:
      return "Items Needed";
  }
});
</script>
<style lang="scss" scoped>
.checklist-item {
  border: 1px solid lightgray;
  padding: 1em;
  margin-bottom: 1em;
  background-color: #eeeeee;

  .check-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .check {
      height: 30px;
      width: 30px;
      border: 1px solid black;
      margin-right: 10px;
      text-align: center;
      font-size: 3em;
      color: green;
    }

    .check-title {
      font-weight: 700;
      font-size: 1.25rem;
      display: flex;
      line-height: 30px;

      .status {
        color: #bb0000;
        margin: 0 10px;

        &.normal {
          color: black;
        }
      }
    }

    .toggle {
      all: unset;
      font-size: 3rem;
      font-weight: 700;
      line-height: 40px;
      cursor: pointer;
    }
  }

  .check-content {
    height: 0;
    display: none;
  }

  &.complete {
    .check-header {
      .check-title {
        .status {
          color: green;
        }
      }
    }
  }

  &.pending {
    .check-header {
      .check-title {
        .status {
          color: #db961e;
        }
      }
    }
  }

  &.active {
    .check-header {
      margin-bottom: 1em;
    }

    .check-content {
      height: fit-content;
      display: block;
    }
  }
}
</style>
